import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiEdit } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";

const DROPDOWN_MIN_WIDTH = 160;

const MoreVerticalIcon = ({ className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={`w-5 h-5 ${className}`}
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
    />
  </svg>
);

const Table = ({
  columns = [],
  data = [],
  onRowClick,
  selectable = false,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  actions = [],
  getRowClassName,
}) => {
  const ROWS_PER_PAGE = 5;
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownAnchorRect, setDropdownAnchorRect] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const dropdownContentRef = useRef(null);

  const isAllSelected = data.length > 0 && selectedRows.length === data.length;
  const hasActions =
    typeof actions === 'function' ? true : actions && actions.length > 0;
  const totalCols = columns.length + (selectable ? 1 : 0) + (hasActions ? 1 : 0);

  // Pagination logic
  const totalPages = Math.ceil(data.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedData = data.slice(startIndex, endIndex);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const portalEl = dropdownContentRef.current;
      if (portalEl && portalEl.contains(event.target)) return;
      setOpenDropdown(null);
      setDropdownAnchorRect(null);
    };

    if (openDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Close dropdown on scroll so position doesn't get stale
  useEffect(() => {
    if (openDropdown === null) return;
    const handleScroll = () => {
      setOpenDropdown(null);
      setDropdownAnchorRect(null);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [openDropdown]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectAll([]);
    } else {
      onSelectAll(data.map((_, index) => index));
    }
  };

  const handleSelectRow = (index, e) => {
    e.stopPropagation();
    const newSelected = selectedRows.includes(index)
      ? selectedRows.filter((i) => i !== index)
      : [...selectedRows, index];
    onSelectRow(newSelected);
  };

  const alignClass = (align) => {
    if (align === 'center') return 'text-center';
    if (align === 'right') return 'text-right';
    return 'text-left';
  };

  const thClass = (colIndex) => {
    const align = alignClass(columns[colIndex - (selectable ? 1 : 0)]?.align);
    const first = colIndex === 0 ? 'rounded-l-[12px] ' : '';
    const last = colIndex === totalCols - 1 ? 'rounded-r-[12px] ' : '';
    return `${first}${last}${align} h-14 whitespace-nowrap border-none font-semibold text-base`;
  };

  const tdClass = (colIndex, rowIndex, row) => {
    const selected = selectedRows.includes(rowIndex);
    const rowClassName = getRowClassName ? getRowClassName(row, rowIndex) : '';
    const isHighlighted = rowClassName && rowClassName.includes('highlight');

    const bg = selected ? 'bg-blue-50' : isHighlighted ? 'bg-yellow-50' : 'bg-transparent';
    const align = alignClass(columns[colIndex - (selectable ? 1 : 0)]?.align);
    const first = colIndex === 0 ? 'rounded-l-lg ' : '';
    const last = colIndex === totalCols - 1 ? 'rounded-r-lg ' : '';
    return `${first}${last}${align} ${bg} whitespace-nowrap border-none text-base font-medium h-16`;
  };

  if (loading) {
    return (
      <div
        className={`wms-pill-table-loading flex justify-center items-center p-8 bg-transparent ${className}`}
        style={{ color: '#101828' }}
        role="status"
        aria-live="polite"
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      className={`wms-pill-table-root w-full overflow-visible ${className}`}
      style={{ backgroundColor: 'transparent', padding: 0 }}
      role="region"
      aria-label="Data table"
    >
      <div
        className="wms-pill-table-card overflow-visible"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #EEF1F5',
          borderRadius: 16,
          boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
          padding: 18,
        }}
      >
        <div className="wms-pill-table-scroll overflow-x-auto overflow-y-visible">
          <table
            className="wms-pill-table w-full"
            style={{ borderSpacing: '0 14px', borderCollapse: 'separate', border: 'none' }}
          >
            <thead>
              <tr>
                {selectable && (
                  <th
                    className={`rounded-l-[12px] h-14 whitespace-nowrap w-10 md:w-12 border-none pl-[22px] pr-[22px] font-semibold text-base ${totalCols === 1 ? 'rounded-r-[12px]' : ''}`}
                    scope="col"
                    style={{ border: 'none', backgroundColor: '#F6F7FA', color: '#98A2B3' }}
                  >
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      className="w-4 h-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                      aria-label="Select all rows"
                    />
                  </th>
                )}

                {columns.map((column, index) => (
                  <th
                    key={index}
                    className={thClass(selectable ? index + 1 : index)}
                    scope="col"
                    style={{
                      border: 'none',
                      backgroundColor: '#F6F7FA',
                      color: '#98A2B3',
                      paddingLeft: 22,
                      paddingRight: 22,
                    }}
                  >
                    {column.label}
                  </th>
                ))}

                {hasActions && (
                  <th
                    className={`${thClass(selectable ? columns.length + 1 : columns.length)} text-right`}
                    scope="col"
                    style={{
                      border: 'none',
                      backgroundColor: '#F6F7FA',
                      color: '#98A2B3',
                      paddingLeft: 22,
                      paddingRight: 22,
                    }}
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, rowIndex) => {
                const actualRowIndex = startIndex + rowIndex;
                const actionsList = (() => {
                  const rowActions = typeof actions === 'function' ? actions(row) : actions;
                  return Array.isArray(rowActions) ? rowActions : [];
                })();

                return (
                  <tr
                    key={actualRowIndex}
                    onClick={() => onRowClick && onRowClick(row, actualRowIndex)}
                    className={`group ${onRowClick ? 'cursor-pointer' : ''}`}
                    role={onRowClick ? 'button' : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRowClick(row, actualRowIndex);
                            }
                          }
                        : undefined
                    }
                    aria-label={
                      onRowClick ? `Row ${actualRowIndex + 1}, click to select` : undefined
                    }
                  >
                    {selectable && (
                      <td
                        className={tdClass(0, actualRowIndex, row)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          border: 'none',
                          borderBottom: '1px solid #F2F4F7',
                          color: '#101828',
                          paddingLeft: 22,
                          paddingRight: 22,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(actualRowIndex)}
                          onChange={(e) => handleSelectRow(actualRowIndex, e)}
                          className="w-4 h-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                          aria-label={`Select row ${actualRowIndex + 1}`}
                        />
                      </td>
                    )}

                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className={tdClass(selectable ? colIndex + 1 : colIndex, actualRowIndex, row)}
                        style={{
                          border: 'none',
                          borderBottom: '1px solid #F2F4F7',
                          color: '#101828',
                          paddingLeft: 22,
                          paddingRight: 22,
                        }}
                      >
                        {column.render
                          ? column.render(row[column.key], row, actualRowIndex)
                          : row[column.key]}
                      </td>
                    ))}

                    {hasActions && (
                      <td
                        className={tdClass(
                          selectable ? columns.length + 1 : columns.length,
                          actualRowIndex,
                          row
                        )}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          border: 'none',
                          borderBottom: '1px solid #F2F4F7',
                          color: '#101828',
                          paddingLeft: 22,
                          paddingRight: 22,
                        }}
                      >
                        {actionsList.length > 0 ? (
                          <div
                            className="relative flex items-center justify-end transition-colors"
                            style={{ gap: 22 }}
                          >
                            {actionsList.slice(0, 2).map((action, i) => {
                              const IconComponent = action.icon;
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(row, actualRowIndex);
                                  }}
                                  className="wms-table-action-btn grid place-items-center h-9 w-9 rounded-lg border-0 bg-transparent active:scale-[0.98] transition"
                                  style={{ color: '#101828' }}
                                  aria-label={action.label}
                                >
                                  {action.label?.toLowerCase().includes('delete') ? (
                                    <MdOutlineDelete className="w-5 h-5" />
                                  ) : action.label?.toLowerCase().includes('edit') ? (
                                    <FiEdit className="w-5 h-5" />
                                  ) : IconComponent ? (
                                    <IconComponent className="w-5 h-5" />
                                  ) : null}
                                </button>
                              );
                            })}

                            {actionsList.length > 2 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openDropdown === actualRowIndex) {
                                    setOpenDropdown(null);
                                    setDropdownAnchorRect(null);
                                  } else {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setDropdownAnchorRect(rect);
                                    setOpenDropdown(actualRowIndex);
                                  }
                                }}
                                className="wms-table-action-btn flex h-9 w-9 items-center justify-center rounded-lg border-0 bg-transparent active:scale-[0.98] transition"
                                style={{ color: '#101828' }}
                                aria-label="More actions"
                              >
                                <MoreVerticalIcon className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        ) : null}
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={totalCols}
                  className="py-10 text-center text-base font-medium rounded-lg"
                  style={{ color: '#98A2B3', paddingLeft: 22, paddingRight: 22 }}
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-start gap-4">
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="h-10 w-10 rounded-xl border border-gray-200 bg-white shadow-sm grid place-items-center text-gray-700 hover:bg-gray-50 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            ‹
          </button>

          {(() => {
            const pages = [];
            let lastShownPage = 0;

            for (let page = 1; page <= totalPages; page++) {
              const showPage =
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1);

              if (showPage) {
                if (page - lastShownPage > 1) {
                  pages.push(
                    <span key={`ellipsis-${page}`} className="px-2 text-gray-500 text-base font-medium">
                      ...
                    </span>
                  );
                }

                pages.push(
                  <button
                    key={page}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={
                      currentPage === page
                        ? 'rounded-xl bg-gray-100 px-4 py-2 text-base font-semibold text-gray-900'
                        : 'px-3 py-2 text-base font-medium text-gray-800'
                    }
                    aria-label={`Page ${page}`}
                    aria-current={currentPage === page ? 'page' : undefined}
                  >
                    {page}
                  </button>
                );
                lastShownPage = page;
              }
            }

            return pages;
          })()}

          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="h-10 w-10 rounded-xl border border-gray-200 bg-white shadow-sm grid place-items-center text-gray-700 hover:bg-gray-50 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
      </div>

      {/* Dropdown (only when more than 2 actions exist) */}
      {openDropdown !== null &&
        dropdownAnchorRect &&
        createPortal(
          (() => {
            const row = data[openDropdown];
            const rowActions = typeof actions === 'function' ? actions(row) : actions;
            const actionsList = Array.isArray(rowActions) ? rowActions : [];
            const extraActions = actionsList.slice(2);

            const rect = dropdownAnchorRect;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openAbove = spaceBelow < 220;

            const style = {
              position: 'fixed',
              left: Math.max(
                8,
                Math.min(rect.right - DROPDOWN_MIN_WIDTH, window.innerWidth - DROPDOWN_MIN_WIDTH - 8)
              ),
              width: DROPDOWN_MIN_WIDTH,
              zIndex: 99999,
              ...(openAbove ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
            };

            return (
              <div
                ref={dropdownContentRef}
                className="min-w-[160px] rounded-xl border border-gray-200 bg-white shadow-lg py-1"
                style={style}
              >
                {extraActions.map((action, actionIndex) => {
                  const IconComponent = action.icon;
                  return (
                    <button
                      key={actionIndex}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick(row, openDropdown);
                        setOpenDropdown(null);
                        setDropdownAnchorRect(null);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 transition hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl"
                    >
                      {IconComponent && (
                        <span className="text-gray-600">
                          <IconComponent className="w-4 h-4" />
                        </span>
                      )}
                      <span>{action.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })(),
          document.body
        )}
    </div>
  );
};

export default Table;
